import { DatePickerInput, DatePickerInputProps } from '@mantine/dates';
import { IconCalendarEvent } from '@tabler/icons-react';
import {
  EN_LOCALE_DATE_FORMAT,
  parseDateUnknown,
} from '@zorro/shared/formatters';
import { ForwardedRef, forwardRef } from 'react';

import { ZorroIcon } from '../../Icon';
import { ReadOnlyLock } from '../../v2/ReadOnlyLock';
import {
  calendarDropdownStyles,
  calendarPanelProps,
  dateInputClassNames,
} from '../DateInput/calendarPanel';

export interface IDateRangeInputProps extends Omit<
  DatePickerInputProps<'range'>,
  'onChange' | 'readOnly'
> {
  isReadOnly?: boolean;
  readOnlyVariant?: 'label' | 'lock';
  onChange: (value: [Date | null, Date | null]) => void;
}

export const ZorroDateRangeInput = forwardRef(
  (
    {
      placeholder = `${EN_LOCALE_DATE_FORMAT} - ${EN_LOCALE_DATE_FORMAT}`,
      size = 'md',
      clearable = false,
      rightSection,
      onChange,
      disabled,
      isReadOnly,
      readOnlyVariant = 'label',
      popoverProps,
      ...props
    }: IDateRangeInputProps,
    ref: ForwardedRef<HTMLButtonElement>
  ) => {
    return (
      <DatePickerInput
        ref={ref}
        type="range"
        classNames={dateInputClassNames}
        valueFormat={EN_LOCALE_DATE_FORMAT}
        placeholder={placeholder}
        size={size}
        clearable={clearable}
        disabled={disabled}
        readOnly={isReadOnly}
        data-readonly-variant={
          isReadOnly && readOnlyVariant === 'lock' ? 'lock' : undefined
        }
        rightSection={
          rightSection ||
          (isReadOnly && readOnlyVariant === 'lock' ? (
            <ReadOnlyLock
              size={20}
              stroke={1.5}
              color="var(--brand-grayscale-600)"
            />
          ) : (
            <ZorroIcon
              icon={IconCalendarEvent}
              color="var(--brand-grayscale-600)"
              size={20}
            />
          ))
        }
        onChange={(value) => {
          if (!onChange) return;
          const dateValue = value.map((val) =>
            val ? parseDateUnknown(val).toDate() : null
          ) as [Date | null, Date | null];
          onChange(dateValue);
        }}
        {...calendarPanelProps}
        {...props}
        popoverProps={{
          styles: calendarDropdownStyles,
          ...popoverProps,
          disabled: disabled || popoverProps?.disabled,
        }}
      />
    );
  }
);

ZorroDateRangeInput.displayName = 'ZorroDateRangeInput';
